import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Phone, Upload, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { BUSINESS } from "../lib/constants";

const SERVICE_OPTIONS = [
  { id: "carpet", label: "Carpet Cleaning" },
  { id: "couch", label: "Couch / Lounge Cleaning" },
  { id: "rug", label: "Rug Cleaning" },
  { id: "mattress", label: "Mattress Cleaning" },
  { id: "tile", label: "Tile & Grout Cleaning" },
  { id: "commercial", label: "Commercial Carpet Cleaning" },
  { id: "pet", label: "Pet Stain / Odour" },
  { id: "water", label: "Water / Flood Damage" },
  { id: "other", label: "Other" },
];

const PROPERTY_OPTIONS = [
  "Single-storey house",
  "Double-storey house",
  "Apartment",
  "Townhouse",
  "Office",
  "Commercial property",
  "Other",
];

const YES_NO_UNSURE = ["Yes", "No", "Not sure"];

const FIELD_GROUPS = {
  carpet: {
    title: "Carpet Cleaning",
    fields: [
      { key: "bedrooms", label: "Number of bedrooms", type: "select", options: ["1", "2", "3", "4", "5+"] },
      { key: "livingAreas", label: "Living / lounge areas", type: "select", options: ["1", "2", "3", "4+"] },
      { key: "hallways", label: "Hallways", type: "radio", options: YES_NO_UNSURE },
      { key: "stairs", label: "Stairs", type: "radio", options: YES_NO_UNSURE },
      { key: "otherAreas", label: "Other carpeted areas", type: "text", placeholder: "e.g. study, rumpus room" },
      { key: "stainsOdours", label: "Any stains or odours?", type: "radio", options: YES_NO_UNSURE },
    ],
  },
  couch: {
    title: "Couch / Lounge Cleaning",
    fields: [
      { key: "seats", label: "Approximate number of seats", type: "select", options: ["1–2", "3", "4", "5", "6+"] },
      { key: "chaise", label: "Does it have a chaise?", type: "radio", options: YES_NO_UNSURE },
      { key: "fabric", label: "Fabric / material (if known)", type: "text", placeholder: "e.g. microfibre, cotton blend" },
      { key: "stainsOdours", label: "Any stains or odours?", type: "radio", options: YES_NO_UNSURE },
      { key: "photos", label: "Photos of your couch (optional)", type: "file" },
    ],
  },
  rug: {
    title: "Rug Cleaning",
    fields: [
      { key: "count", label: "Number of rugs", type: "select", options: ["1", "2", "3", "4+"] },
      { key: "sizes", label: "Approximate sizes", type: "text", placeholder: "e.g. 2m x 3m, hallway runner" },
      { key: "material", label: "Material (if known)", type: "select", options: ["Wool", "Synthetic", "Cotton", "Blend", "Not sure"] },
      { key: "stains", label: "Any stains?", type: "radio", options: YES_NO_UNSURE },
      { key: "petOdour", label: "Pet odour?", type: "radio", options: YES_NO_UNSURE },
      { key: "photos", label: "Photos of your rug (optional)", type: "file" },
    ],
  },
  mattress: {
    title: "Mattress Cleaning",
    fields: [
      { key: "size", label: "Mattress size", type: "select", options: ["Single", "King Single", "Double", "Queen", "King"] },
      { key: "count", label: "Number of mattresses", type: "select", options: ["1", "2", "3", "4+"] },
      { key: "concerns", label: "Specific stain / odour concerns", type: "textarea", placeholder: "e.g. urine accident last week, general freshening" },
    ],
  },
  tile: {
    title: "Tile & Grout Cleaning",
    fields: [
      { key: "areas", label: "Areas requiring cleaning", type: "text", placeholder: "e.g. kitchen, bathroom, hallway" },
      { key: "approxSize", label: "Approximate size", type: "text", placeholder: "e.g. 40m², small bathroom" },
      { key: "indoorOutdoor", label: "Indoor or outdoor?", type: "select", options: ["Indoor", "Outdoor", "Both"] },
      { key: "condition", label: "Condition / problem description", type: "textarea", placeholder: "e.g. dark grout lines, grease build-up near cooktop" },
    ],
  },
  commercial: {
    title: "Commercial Carpet Cleaning",
    fields: [
      { key: "premisesType", label: "Business / property type", type: "text", placeholder: "e.g. retail shop, strata common areas" },
      { key: "approxArea", label: "Approximate area", type: "text", placeholder: "e.g. 300m²" },
      { key: "areas", label: "Areas requiring cleaning", type: "textarea", placeholder: "e.g. entrance, corridors, showroom carpet" },
      { key: "timing", label: "Preferred timing", type: "text", placeholder: "e.g. before opening hours" },
      { key: "frequency", label: "Frequency (if relevant)", type: "select", options: ["One-off", "Weekly", "Fortnightly", "Monthly", "Not sure"] },
    ],
  },
  pet: {
    title: "Pet Stain / Odour",
    fields: [
      { key: "surfaces", label: "What is affected?", type: "text", placeholder: "e.g. lounge room carpet, rug, couch" },
      { key: "what", label: "Tell us about the problem", type: "textarea", placeholder: "What happened, roughly how long ago, any previous treatments" },
      { key: "photos", label: "Photos (optional)", type: "file" },
    ],
  },
  water: {
    title: "Water / Flood Damage",
    fields: [
      { key: "what", label: "What happened?", type: "textarea", placeholder: "e.g. burst pipe in the laundry overnight" },
      { key: "when", label: "When did the water damage occur?", type: "text", placeholder: "e.g. last night, two days ago" },
      { key: "areasAffected", label: "Which areas are affected?", type: "text", placeholder: "e.g. bedroom carpet and hallway" },
      { key: "carpetWet", label: "Is the carpet wet?", type: "radio", options: YES_NO_UNSURE },
      { key: "approxArea", label: "Approximate affected area", type: "text", placeholder: "e.g. one room, 20m²" },
      { key: "sourceStopped", label: "Is the water source stopped?", type: "radio", options: YES_NO_UNSURE },
      { key: "photos", label: "Photos (very helpful, optional)", type: "file" },
    ],
  },
  other: {
    title: "Other Cleaning",
    fields: [
      { key: "description", label: "Tell us what you need cleaned", type: "textarea", placeholder: "Describe the job — photos help too" },
    ],
  },
};

const STEPS = ["Service", "Property", "Details", "Contact"];

const inputCls =
  "w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-ink placeholder:text-brand-muted/60 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-[border-color,box-shadow] duration-300";

const Label = ({ children }) => (
  <span className="block text-xs font-bold tracking-[0.08em] uppercase text-brand-muted mb-2">{children}</span>
);

export const EstimateForm = ({ compact = false }) => {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [services, setServices] = useState([]);
  const [property, setProperty] = useState("");
  const [details, setDetails] = useState({});
  const [contact, setContact] = useState({ suburb: "", postcode: "", date: "", notes: "", photos: "", firstName: "", lastName: "", mobile: "", email: "", company: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [liftAnswer, setLiftAnswer] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]);

  const activeGroups = useMemo(() => services.filter((s) => FIELD_GROUPS[s]), [services]);

  const toggleService = (id) =>
    setServices((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const setDetail = (group, key, value) =>
    setDetails((prev) => ({ ...prev, [group]: { ...(prev[group] || {}), [key]: value } }));

  const setC = (key) => (e) => setContact((prev) => ({ ...prev, [key]: e.target.value }));

  const next = () => {
    setError("");
    if (step === 0 && services.length === 0) return setError("Please choose at least one service.");
    if (step === 1 && !property) return setError("Please select your property type.");
    if (step === 1 && property === "Apartment" && !liftAnswer) return setError("Please let us know if a lift is available.");
    if (step === 3) {
      if (!contact.firstName.trim()) return setError("Please enter your first name.");
      if (!/^[\d\s()+]{8,}$/.test(contact.mobile.trim())) return setError("Please enter a valid mobile number.");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return setError("Please enter a valid email address.");
      if (!contact.suburb.trim()) return setError("Please enter your suburb.");
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!contact.firstName.trim()) return setError("Please enter your first name.");
    if (!/^[\d\s()+]{8,}$/.test(contact.mobile.trim())) return setError("Please enter a valid mobile number.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) return setError("Please enter a valid email address.");
    if (!contact.suburb.trim()) return setError("Please enter your suburb.");

    const serviceLabels = services.map((id) => SERVICE_OPTIONS.find((o) => o.id === id).label);
    const labelledDetails = {};
    activeGroups.forEach((g) => {
      const fields = {};
      FIELD_GROUPS[g].fields.forEach((f) => {
        const v = details[g]?.[f.key];
        if (v) fields[f.label] = v;
      });
      if (Object.keys(fields).length) labelledDetails[FIELD_GROUPS[g].title] = fields;
    });

    const payload = {
      services: serviceLabels,
      propertyType: property,
      liftAvailable: property === "Apartment" ? liftAnswer : "",
      details: labelledDetails,
      suburb: contact.suburb,
      postcode: contact.postcode,
      date: contact.date,
      notes: contact.notes,
      photos: contact.photos,
      firstName: contact.firstName,
      lastName: contact.lastName,
      mobile: contact.mobile,
      email: contact.email,
      company: contact.company,
    };

    if (photoFiles.length > 5) return setError("Please attach no more than 5 photos in total.");
    for (const file of photoFiles) {
      if (!file.type.startsWith("image/")) return setError("Only image files can be attached.");
      if (file.size > 8 * 1024 * 1024) return setError(`"${file.name}" is over 8 MB — please choose a smaller photo.`);
    }

    setSending(true);
    try {
      const ENQUIRY_ENDPOINT = process.env.REACT_APP_ENQUIRY_ENDPOINT || `${process.env.REACT_APP_BACKEND_URL}/api/enquiry`;
      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      photoFiles.forEach((file) => formData.append("photos", file));
      const res = await fetch(ENQUIRY_ENDPOINT, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "send failed");
      }
      setDone(true);
      toast.success("Enquiry sent", { description: "Thanks — Kay's Carpet Cleaning has received your enquiry." });
      if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1 });
    } catch (err) {
      setError(`We couldn't send your enquiry just now. Please try again, or call us directly on ${BUSINESS.phoneDisplay} — you can also email ${BUSINESS.email}.`);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-brand-line/70 rounded-[1.75rem] p-8 sm:p-12 text-center shadow-[0_30px_90px_rgba(45,90,83,0.14)]" data-testid="estimate-success">
        <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-brand text-white mb-6">
          <Check size={30} />
        </span>
        <h3 className="font-display font-medium text-2xl sm:text-3xl tracking-tight text-brand-ink">
          Thanks! We've received your enquiry.
        </h3>
        <p className="mt-4 text-brand-muted text-base leading-relaxed max-w-md mx-auto">
          Kay's Carpet Cleaning will be in touch soon.
        </p>
        <p className="mt-3 text-base text-brand-muted leading-relaxed max-w-md mx-auto">
          Need to speak with us? Call{" "}
          <a href={BUSINESS.phoneHref} className="text-brand font-semibold underline underline-offset-2" data-testid="estimate-success-call">
            {BUSINESS.phoneDisplay}
          </a>
        </p>
        <p className="mt-3 text-sm text-brand-muted leading-relaxed max-w-md mx-auto">
          If you attached photos, they're already on their way to us with your enquiry.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-brand-line/70 rounded-[1.75rem] p-6 sm:p-10 shadow-[0_30px_90px_rgba(45,90,83,0.14)]" data-testid="estimate-form">
      {!compact && (
        <div className="mb-8">
          <h3 className="font-display font-medium text-2xl sm:text-3xl tracking-tight text-brand-ink">Quick Cleaning Estimate</h3>
          <p className="mt-2 text-sm text-brand-muted leading-relaxed">
            Tell us a little about the job. The more information you provide, the easier it is for us to understand what you need.
          </p>
        </div>
      )}

      <ol className="flex items-center gap-2 mb-8" aria-label="Form progress">
        {STEPS.map((s, i) => (
          <li key={s} className="flex items-center gap-2 flex-1">
            <span
              className={`w-7 h-7 shrink-0 rounded-full text-xs font-bold flex items-center justify-center transition-colors duration-300 ${
                i < step ? "bg-brand text-white" : i === step ? "bg-brand text-white ring-4 ring-brand-mist" : "bg-brand-mist text-brand-muted"
              }`}
              data-testid={`estimate-step-indicator-${i + 1}`}
            >
              {i < step ? <Check size={13} /> : i + 1}
            </span>
            <span className={`text-xs font-semibold hidden sm:block ${i === step ? "text-brand-ink" : "text-brand-muted"}`}>{s}</span>
            {i < STEPS.length - 1 && <span className="flex-1 h-px bg-brand-line" />}
          </li>
        ))}
      </ol>

      <form onSubmit={submit} noValidate>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <fieldset>
                <Label>What do you need cleaned? <span className="normal-case font-medium">(choose all that apply)</span></Label>
                <div className="flex flex-wrap gap-2.5" data-testid="estimate-service-options">
                  {SERVICE_OPTIONS.map((o) => (
                    <button
                      type="button"
                      key={o.id}
                      onClick={() => toggleService(o.id)}
                      aria-pressed={services.includes(o.id)}
                      data-testid={`estimate-service-${o.id}`}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-[background-color,border-color,color] duration-300 ${
                        services.includes(o.id)
                          ? "bg-brand text-white border-brand"
                          : "bg-white text-brand-ink border-brand-line hover:border-brand"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                {services.includes("water") && (
                  <p className="mt-5 flex items-start gap-2.5 text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="estimate-water-safety-note">
                    <ShieldAlert size={18} className="shrink-0 mt-0.5" />
                    If there is an electrical or immediate safety risk, stay clear of the affected area.
                  </p>
                )}
              </fieldset>
            )}

            {step === 1 && (
              <fieldset>
                <Label>What type of property is it?</Label>
                <div className="grid sm:grid-cols-2 gap-2.5" data-testid="estimate-property-options">
                  {PROPERTY_OPTIONS.map((p) => (
                    <button
                      type="button"
                      key={p}
                      onClick={() => { setProperty(p); if (p !== "Apartment") setLiftAnswer(""); }}
                      aria-pressed={property === p}
                      data-testid={`estimate-property-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`px-4 py-3.5 rounded-xl text-sm font-medium border text-left transition-[background-color,border-color,color] duration-300 ${
                        property === p ? "bg-brand text-white border-brand" : "bg-white text-brand-ink border-brand-line hover:border-brand"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                {property === "Apartment" && (
                  <div className="mt-5" data-testid="estimate-lift-question">
                    <Label>Is there a lift available? *</Label>
                    <div className="flex flex-wrap gap-2">
                      {YES_NO_UNSURE.map((o) => (
                        <button
                          type="button"
                          key={o}
                          onClick={() => setLiftAnswer(o)}
                          aria-pressed={liftAnswer === o}
                          data-testid={`estimate-lift-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors duration-300 ${
                            liftAnswer === o ? "bg-brand text-white border-brand" : "bg-white text-brand-muted border-brand-line hover:border-brand"
                          }`}
                        >
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </fieldset>
            )}

            {step === 2 && (
              <div className="space-y-8" data-testid="estimate-details-step">
                {activeGroups.length === 0 && (
                  <p className="text-sm text-brand-muted">No extra details needed — continue to your contact details.</p>
                )}
                {activeGroups.map((g) => (
                  <fieldset key={g} className="border-t border-brand-line/60 pt-6 first:border-0 first:pt-0">
                    <h4 className="font-display font-semibold text-base text-brand-ink mb-4">{FIELD_GROUPS[g].title}</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {FIELD_GROUPS[g].fields.map((f) => (
                        <div key={f.key} className={f.type === "textarea" || f.type === "file" ? "sm:col-span-2" : ""}>
                          <Label>{f.label}</Label>
                          {f.type === "text" && (
                            <input type="text" className={inputCls} placeholder={f.placeholder} value={details[g]?.[f.key] || ""} onChange={(e) => setDetail(g, f.key, e.target.value)} data-testid={`estimate-${g}-${f.key}`} />
                          )}
                          {f.type === "textarea" && (
                            <textarea rows={3} className={`${inputCls} resize-none`} placeholder={f.placeholder} value={details[g]?.[f.key] || ""} onChange={(e) => setDetail(g, f.key, e.target.value)} data-testid={`estimate-${g}-${f.key}`} />
                          )}
                          {f.type === "select" && (
                            <select className={inputCls} value={details[g]?.[f.key] || ""} onChange={(e) => setDetail(g, f.key, e.target.value)} data-testid={`estimate-${g}-${f.key}`}>
                              <option value="">Select…</option>
                              {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          )}
                          {f.type === "radio" && (
                            <div className="flex flex-wrap gap-2">
                              {f.options.map((o) => (
                                <button
                                  type="button"
                                  key={o}
                                  onClick={() => setDetail(g, f.key, o)}
                                  aria-pressed={details[g]?.[f.key] === o}
                                  data-testid={`estimate-${g}-${f.key}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors duration-300 ${
                                    details[g]?.[f.key] === o ? "bg-brand text-white border-brand" : "bg-white text-brand-muted border-brand-line hover:border-brand"
                                  }`}
                                >
                                  {o}
                                </button>
                              ))}
                            </div>
                          )}
                          {f.type === "file" && (
                            <div>
                              <label className="inline-flex items-center gap-2 cursor-pointer border border-dashed border-brand-line rounded-xl px-4 py-3 text-sm text-brand-muted hover:border-brand transition-colors duration-300" data-testid={`estimate-${g}-${f.key}-upload`}>
                                <Upload size={16} className="text-brand" />
                                {details[g]?.[f.key] ? details[g][f.key] : "Choose photos…"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="sr-only"
                                  onChange={(e) => {
                                    const picked = Array.from(e.target.files || []);
                                    setDetail(g, f.key, picked.map((x) => x.name).join(", "));
                                    setPhotoFiles((prev) => [...prev, ...picked]);
                                  }}
                                />
                              </label>
                              <p className="mt-1.5 text-xs text-brand-muted">Photos are attached to your enquiry email automatically (up to 5 images, 8 MB each).</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}

            {step === 3 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="estimate-contact-step">
                <div>
                  <Label>Gold Coast suburb *</Label>
                  <input type="text" className={inputCls} placeholder="e.g. Robina" value={contact.suburb} onChange={setC("suburb")} data-testid="estimate-suburb-input" />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <input type="text" inputMode="numeric" className={inputCls} placeholder="e.g. 4064" value={contact.postcode} onChange={setC("postcode")} data-testid="estimate-postcode-input" />
                </div>
                <div>
                  <Label>Preferred date (optional)</Label>
                  <input type="date" min={new Date().toISOString().split("T")[0]} className={inputCls} value={contact.date} onChange={setC("date")} data-testid="estimate-date-input" />
                </div>
                <div>
                  <Label>Photo upload (optional)</Label>
                  <label className="flex items-center gap-2 cursor-pointer border border-dashed border-brand-line rounded-xl px-4 py-3 text-sm text-brand-muted hover:border-brand transition-colors duration-300" data-testid="estimate-contact-photos-upload">
                    <Upload size={16} className="text-brand shrink-0" />
                    <span className="truncate">{contact.photos || "Choose photos…"}</span>
                    <input type="file" accept="image/*" multiple className="sr-only" onChange={(e) => {
                      const picked = Array.from(e.target.files || []);
                      setContact((p) => ({ ...p, photos: picked.map((x) => x.name).join(", ") }));
                      setPhotoFiles((prev) => [...prev, ...picked]);
                    }} />
                  </label>
                </div>
                <div className="sm:col-span-2">
                  <Label>Anything else we should know?</Label>
                  <textarea rows={3} className={`${inputCls} resize-none`} placeholder="Access details, parking, questions — anything helpful." value={contact.notes} onChange={setC("notes")} data-testid="estimate-notes-input" />
                </div>
                <div>
                  <Label>First name *</Label>
                  <input type="text" autoComplete="given-name" className={inputCls} value={contact.firstName} onChange={setC("firstName")} data-testid="estimate-firstname-input" />
                </div>
                <div>
                  <Label>Last name</Label>
                  <input type="text" autoComplete="family-name" className={inputCls} value={contact.lastName} onChange={setC("lastName")} data-testid="estimate-lastname-input" />
                </div>
                <div>
                  <Label>Mobile *</Label>
                  <input type="tel" autoComplete="tel" className={inputCls} placeholder="0400 000 000" value={contact.mobile} onChange={setC("mobile")} data-testid="estimate-mobile-input" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <input type="email" autoComplete="email" className={inputCls} placeholder="you@example.com" value={contact.email} onChange={setC("email")} data-testid="estimate-email-input" />
                </div>
                <input type="text" name="company" tabIndex={-1} autoComplete="off" className="hidden" value={contact.company} onChange={setC("company")} aria-hidden="true" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && (
          <p className="mt-5 text-sm font-medium text-red-600" role="alert" data-testid="estimate-error">{error}</p>
        )}

        <div className="mt-8 flex items-center justify-between gap-4">
          {step > 0 ? (
            <button type="button" onClick={() => { setError(""); setStep((s) => s - 1); }} data-testid="estimate-back-button" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-muted hover:text-brand transition-colors duration-300">
              <ArrowLeft size={16} /> Back
            </button>
          ) : <span />}
          {step < 3 ? (
            <button type="button" onClick={next} data-testid="estimate-next-button" className="group inline-flex items-center gap-2.5 bg-brand hover:bg-brand-dark text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-[background-color,transform] duration-300 hover:scale-[0.97]">
              Continue <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          ) : (
            <button type="submit" disabled={sending} data-testid="estimate-submit-button" className="group inline-flex items-center gap-2.5 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-semibold px-8 py-4 rounded-full text-base shadow-[0_10px_30px_rgba(11,114,133,0.28)] transition-[background-color,transform] duration-300 hover:scale-[0.97]">
              {sending ? "Sending…" : "Get My Estimate"} <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          )}
        </div>
      </form>

      <p className="mt-6 text-center text-xs text-brand-muted">
        Urgent job? Call <a href={BUSINESS.phoneHref} className="font-semibold text-brand underline underline-offset-2" data-testid="estimate-form-call-link">{BUSINESS.phoneDisplay}</a> — {BUSINESS.hoursShort}.
      </p>
    </div>
  );
};
